import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import Progress from "./generic/Progress";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import NavigationHandler from "./application/NavigationHandler";
import NotificationContainer from "../common/notification/NotificationContainer";
import LoadingContainer from "../common/loading/LoadingContainer";
import { DocPropKeys, retrieveDocumentProp } from "../utils/helpers/CustomXmlHelper";
import { useAppDispatch, useAppSelector } from "../redux/hooks/hooks";
import {
  selectAuthData,
  selectBackendUrl,
  selectClientId,
  selectCpmMetadata,
  selectCpmUser,
  selectIsAuthenticated,
  selectIxbrlSettings,
  selectTenantId,
  selectUserFunctions,
  setCpmMetadata,
  setCpmUser,
  setIxbrlSettings,
  setProtectionType,
  setSeahorseDocumentLinked,
  setServiceProvider,
  setUserDocumentCheckout,
} from "../redux/feature/settings/configurations-slice";
import { openEventChannelAsync, selectElaborationStatusList } from "../redux/feature/object/remote-elaborations-slice";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../utils/constants/eventChannel";
import { AppDispatch, store } from "../redux/store/store";
import { useWindowSize } from "../utils/helpers/useWindowSizeHook";
import { ViewportTooNarrowError } from "./generic/ViewportTooNarrowError";
import { fetchIxbrlSettings, fetchSeahorseDocumentLinked, fetchServiceProvider } from "../pages/seahorse/SeahorseUtils";
import { isNotAuthorizedUser } from "../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../api/models/user-function-enum";
import { fetchCpmUser } from "../api/document/CpmUser";
import StandaloneModalContainer from "../common/standaloneModal/StandaloneModalContainer";
import { PanelStandaloneContainer } from "../common/panelStandaloneModal/PanelStandaloneContainer";
import { fetchDocumentUrl } from "../api/document/Documents";
import { DocumentPartSettings } from "../api/models/document-part-settings";
import { isInDesignEnabled } from "../api/document/InDesign";
import { isRagIndexingEnabled } from "../api/document/RagIndexing";
import { fetchCurrentUserDocumentCheckout, updateSettings } from "../api/document/DocumentsPart";
import { disablePendo, hashVisitorId, initializePendo, injectPendoScript } from "../common/pendo/PendoService";
import { useHideImagesOnError } from "../utils/helpers/useHideImagesOnError";
import { fetchRefreshCache } from "../api/document/RefreshCache";
import { initializeLogger } from "../common/logger";
import { historyNavigation } from "../redux/feature/general/navigation-slice";
import { isSelfScrollPage } from "../utils/constants/navigation";

const MIN_SUPPORTED_WIDTH = 260;
const MIN_SUPPORTED_HEIGHT = 300;

const classNames = mergeStyleSets({
  pageContainer: {
    position: "relative",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },

  contentWrap: {
    maxHeight: "90vh",
    paddingBottom: "2.5rem",
    overflow: "auto",
    flexGrow: 1,
  },

  content: {
    height: "100%",
  },
  footer: {
    position: "absolute",
    bottom: "0",
    width: "100%",
    height: "2.5rem",
  },
});

export interface AppProps {
  title: string;
  isOfficeInitialized: boolean;
}

const App: React.FunctionComponent<AppProps> = (props) => {
  const { title, isOfficeInitialized } = props;

  const dispatch: AppDispatch = useAppDispatch();

  const authData = useAppSelector(selectAuthData);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectCpmUser);
  const cpmMetadata = useAppSelector(selectCpmMetadata);
  const tenantId = useAppSelector(selectTenantId);
  const userFunctions = useAppSelector(selectUserFunctions);
  const ixbrlSettings = useAppSelector(selectIxbrlSettings);
  const clientId = useAppSelector(selectClientId);
  const backendUrl = useAppSelector(selectBackendUrl);
  const elaborations = useAppSelector(selectElaborationStatusList);
  const pageHistory = useAppSelector(historyNavigation);
  const [channelId, setChannelId] = useState(undefined);
  const currentPage = pageHistory?.[pageHistory.length - 1];
  // When the current page manages its own bounded scroll region, the shell must not also scroll,
  // otherwise the shell's contentWrap and the page's inner scroll container each show a scrollbar,
  // producing a nested/double scrollbar (IDS-7240). See isSelfScrollPage for the page list.
  const selfScrollPage = isSelfScrollPage(currentPage);

  useHideImagesOnError();

  useEffect(() => {
    if (tenantId && isAuthenticated) {
      fetchCpmUser()
        .then((result) => {
          const { cpmUser, cpmMetadata } = result;
          // aggiungo il tenantId al metadata (se serve)
          cpmUser.tenantId = tenantId;
          store.dispatch(setCpmUser(cpmUser));
          store.dispatch(
            setCpmMetadata({
              metadata: cpmMetadata,
              tenantId,
            })
          );
          console.log("fetchCpmUser resolved");
          initializeLogger({
            debugEnabled: () => sessionStorage.getItem("debugEnabled") === "true",
          });
        })
        .catch((err) => {
          console.warn("fetchCpmUser failed:", err);
        });
    }
  }, [isAuthenticated, tenantId]);

  useEffect(() => {
    if (cpmMetadata?.pendoEnabled === true) {
      injectPendoScript()
        .then(async () => {
          if (user) {
            const visitorId = await hashVisitorId(user.userName);
            const accountId = user.tenantId;
            initializePendo(visitorId, accountId);
          } else {
            disablePendo();
          }
        })
        .catch((error) => console.error("Pendo initialization failed:", error));
    } else {
      disablePendo();
    }
  }, [user, cpmMetadata]);

  useEffect(() => {
    let notAuthorizedUserForIXBRL =
      isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_ADMIN, userFunctions) &&
      isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_USER, userFunctions);

    if (!notAuthorizedUserForIXBRL && isAuthenticated) {
      fetchServiceProvider().then((result) => store.dispatch(setServiceProvider(result)));
      fetchSeahorseDocumentLinked().then((result) => store.dispatch(setSeahorseDocumentLinked(result)));
      fetchIxbrlSettings().then((result) => store.dispatch(setIxbrlSettings(result)));
    }
  }, [user, isAuthenticated, userFunctions]);

  useEffect(() => {
    // retrieves documentId from documentProps and sets channelId to documentId.
    // If no docId is present EVENT_CHANNEL_DEFAULT_TOPIC is used
    if (isOfficeInitialized)
      retrieveDocumentProp(DocPropKeys.DocumentId).then((documentId) => {
        setChannelId(documentId ?? EVENT_CHANNEL_DEFAULT_TOPIC);
      });
  }, [isOfficeInitialized]);

  useEffect(() => {
    if (channelId && user && clientId && authData && !elaborations) {
      console.log("requesting sse channel open ...");
      dispatch(
        openEventChannelAsync({
          documentId: channelId,
          userId: user.userName,
          baseUrl: backendUrl,
          clientId,
        })
      );
    }
  }, [user, clientId, channelId, authData, isAuthenticated, elaborations]);

  useEffect(() => {
    const retrieve = async () => {
      // fetch and set into the store
      const documentId = await retrieveDocumentProp(DocPropKeys.DocumentId);
      if (!documentId) {
        return;
      }
      const protectionType = await fetchDocumentUrl(documentId);
      store.dispatch(setProtectionType(protectionType.protectionType));

      console.log("sync document settings ...");

      const documentIxbrlSettings = ixbrlSettings?.find((setting) => setting.documentId === documentId);
      const isIxbrlEnabledForDoc = documentIxbrlSettings?.isChecked ?? false;

      const currentSettings: DocumentPartSettings = {
        isInDesignEnabled: await isInDesignEnabled(),
        isRagIndexingEnabled: await isRagIndexingEnabled(),
        isIxbrlEnabled: isIxbrlEnabledForDoc,
      };
      updateSettings(documentId, currentSettings).catch((error) => {
        console.error("Error updating document settings:", error);
      });
    };
    void retrieve();
  }, []);

  useEffect(() => {
    if (tenantId && isAuthenticated) {
      fetchCurrentUserDocumentCheckout()
        .then((userCheckoutDocument) => {
          store.dispatch(setUserDocumentCheckout(userCheckoutDocument));
        })
        .catch((reason) => {
          console.log("Error:", reason);
        });
      const timer = setInterval(() => {
        fetchRefreshCache()
          .then((result) => {
            console.log("result", result);
          })
          .catch((reason) => {
            console.log("Error:", reason);
          });
        fetchCurrentUserDocumentCheckout()
          .then((userCheckoutDocument) => {
            store.dispatch(setUserDocumentCheckout(userCheckoutDocument));
          })
          .catch((reason) => {
            console.log("Error:", reason);
          });
      }, 120000);
      return () => clearInterval(timer);
    }
  }, [isAuthenticated]);

  const { width, height } = useWindowSize();

  const isViewPortSmall = useMemo(
    () => width <= MIN_SUPPORTED_WIDTH || height <= MIN_SUPPORTED_HEIGHT,
    [width, height]
  );

  if (!isOfficeInitialized) {
    return (
      <Progress
        title={title}
        logo={require("../../../../assets/logo-filled.png")}
        message="Please sideload your addin to see app body."
      />
    );
  } else {
    return (
      <>
        {isViewPortSmall && <ViewportTooNarrowError />}
        <LoadingContainer />
        <PanelStandaloneContainer />
        <div className={classNames.pageContainer}>
          <div
            className={classNames.contentWrap}
            style={
              selfScrollPage
                ? {
                    maxHeight: "none",
                    height: "90vh",
                    overflow: "hidden",
                    paddingBottom: 0,
                    boxSizing: "border-box",
                  }
                : undefined
            }
          >
            <div
              className={classNames.content}
              style={selfScrollPage ? { display: "flex", flexDirection: "column", minHeight: 0 } : undefined}
            >
              <NavigationHandler />
            </div>
          </div>
        </div>
        <NotificationContainer />
        <StandaloneModalContainer />
      </>
    );
  }
};

export default App;
